import React, { useEffect, useState } from "react";
import axios from "axios";
import { Container, Table, Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import { FaPencilAlt, FaCheck, FaPrint, FaTimes } from "react-icons/fa";
import { FaTruckFast } from "react-icons/fa6";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../Context/AuthContext";
import { Base_URL } from "../url";
import "../Styling/rawleads.css";

// ViewCreditNotes
// - Fetches unposted credit notes from: GET `${Base_URL}/CreditNote/unpostedCreditNotes`
// - Renders a responsive table
// - Split into Credit Note and Debit Note on the basis of Type (transactionType)

export default function ViewCreditNotes() {
  const [creditNotes, setCreditNotes] = useState([]);
  const [loadingMap, setLoadingMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancelledOnly, setShowCancelledOnly] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${Base_URL}/CreditNote/unpostedCreditNotes`);
        if (mounted) setCreditNotes(res.data || []);
      } catch (err) {
        console.error(err);
        setError("Failed to load credit notes");
        toast.error("❌ Error fetching credit notes");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => (mounted = false);
  }, []);

  const filtered = showCancelledOnly
    ? creditNotes.filter(
        (c) =>
          c.cancelledIndicator === "Yes" ||
          c.cancelledIndicator === "Y" ||
          c.cancelledIndicator === "yes"
      )
    : creditNotes;

  // Split into Credit & Debit based on Type (transactionType)
  const creditTypeNotes = filtered.filter((c) =>
    c.transactionType?.toLowerCase().includes("credit")
  );

  const debitTypeNotes = filtered.filter((c) =>
    c.transactionType?.toLowerCase().includes("debit")
  );

  async function handleMarkPosted(creditNoteId) {
    setLoadingMap((p) => ({ ...p, [creditNoteId]: true }));
    try {
      const changedBy = String(user?.userId ?? "");
      // endpoint assumed; change if your backend expects different route
      await axios.post(`${Base_URL}/CreditNote/MarkAsPosted`, {
        creditNoteId,
        changedBy,
      });
      toast.success(`✅ Credit Note ${creditNoteId} marked as posted`);
      setCreditNotes((prev) =>
        prev.filter((c) => c.creditNoteId !== creditNoteId)
      );
    } catch (err) {
      console.error(err);
      toast.error(`❌ Error marking ${creditNoteId} as posted`);
    } finally {
      setLoadingMap((p) => ({ ...p, [creditNoteId]: false }));
    }
  }

  async function handleCancel(creditNoteId) {
    setLoadingMap((p) => ({ ...p, [creditNoteId]: true }));
    try {
      const changedBy = String(user?.userId ?? "");
      await axios.put(
        `${Base_URL}/CreditNote/cancelCreditNote/id?id=${creditNoteId}`
      );
      toast.success(`✅ Credit Note ${creditNoteId} cancelled`);
      // refresh list locally
      setCreditNotes((prev) =>
        prev.filter((c) => c.creditNoteId !== creditNoteId)
      );
    } catch (err) {
      console.error(err);
      toast.error(`❌ Error cancelling ${creditNoteId}`);
    } finally {
      setLoadingMap((p) => ({ ...p, [creditNoteId]: false }));
    }
  }

  const handleEdit = (creditNote) => {
    navigate(`/credit-notes/edit/${creditNote.creditNoteId}`, {
      state: { creditNote },
    });
  };

const handlePrint = (cn) => {
  const type = cn?.transactionType?.toLowerCase();
  console.log(cn);
  if (type?.includes("credit")) {
    navigate(`/creditNotePrint/${cn.creditNoteId}`);
  } else if (type?.includes("debit")) {
    navigate(`/debitNotePrint/${cn.creditNoteId}`);
  } else {
    toast.error("Unknown transaction type");
  }
};


  const handleInventoryPosting = async(creditNoteId) => {
    try{
       const res=await axios.post(`${Base_URL}/CreditNote/updateInventoryForDebitNote/${creditNoteId}`)
        toast.success(`Inventory Posted Successfully ${creditNoteId}`);
    }
    catch(err){
     console.error(err);
    }
  };

  return (
    <div className="main-content expand">
      <Container fluid className="list-container">
        <div className="lead-dashboard container-fluid mt-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h4 className="fw-bold">📋 Credit/Debit Notes Management</h4>
              <p className="text-muted">View and manage unposted credit/debit notes</p>
            </div>
            <div>
              <Button variant="outline-primary" className="me-2">
                📊 Export Data
              </Button>
              <Button className="btn-bulk me-2">⚡ Bulk Actions</Button>
              <Button
                className="btn-assign me-2"
                onClick={async () => {
                  setLoading(true);
                  try {
                    const res = await axios.get(
                      `${Base_URL}/CreditNote/unpostedCreditNotes`
                    );
                    setCreditNotes(res.data || []);
                    toast.success("🔄 Refreshed");
                  } catch (err) {
                    toast.error("❌ Refresh failed");
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                🔄 Refresh
              </Button>

              <Form.Check
                type="checkbox"
                id="show-cancelled-only"
                label="Show Cancelled Only"
                className="d-inline-block ms-2"
                checked={showCancelledOnly}
                onChange={(e) => setShowCancelledOnly(e.target.checked)}
                style={{ transform: "scale(1.1)", cursor: "pointer" }}
              />
            </div>
          </div>

          <div className="d-flex gap-3 mb-4">
            <div className="stat-card">
              <h5 className="text-primary">{creditNotes.length}</h5>
              <p className="text-muted">Total Unposted</p>
            </div>
            <div className="stat-card">
              <h5 className="text-primary">
                {creditNotes.filter(
                  (inv) => inv.transactionType === "Invoice"
                ).length}
              </h5>
              <p className="text-muted">Invoice Type</p>
            </div>
            <div className="stat-card">
              <h5 className="text-primary">
                {creditNotes.filter((inv) =>
                  moment(inv.invoiceDate).isSame(moment(), "month")
                ).length}
              </h5>
              <p className="text-muted">This Month</p>
            </div>
          </div>
        </div>

        <div className="raw-leads-table">
          {loading ? (
            <p className="no-data text-center py-4">
              Loading credit notes...
            </p>
          ) : (
            <>
              {/* CREDIT NOTES SECTION */}
              <h5 className="mb-2">Credit Notes</h5>
              {creditTypeNotes.length > 0 ? (
                <Table bordered hover responsive className="data-table mb-4">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>CreditNote ID</th>
                      <th>Company Code</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Customer Code</th>
                      <th>Against Invoice Number</th>
                      <th>Credit Note Date</th>
                      <th>Cancelled Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditTypeNotes.map((cn, idx) => (
                      <tr key={cn.creditNoteId || `credit-${idx}`}>
                        <td>{idx + 1}</td>
                        <td>{cn.creditNoteId}</td>
                        <td>{cn.companyCode}</td>
                        <td>{cn.transactionType}</td>
                        <td>{cn.category}</td>
                        <td>{cn.customerCode || "N/A"}</td>
                        <td>{cn.invoiveNumber || cn.invoiceNumber || "-"}</td>
                        <td>
                          {cn.creditNoteDate
                            ? moment(cn.creditNoteDate).format("L")
                            : "-"}
                        </td>
                        <td>
                          {cn.cancelledIndicator ??
                            cn.cancelled_Indicator ??
                            "-"}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            {/* Uncomment if you want Edit / Mark Posted */}
                            {/* <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEdit(cn)}
                              title="Edit"
                            >
                              <FaPencilAlt />
                            </Button>

                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleMarkPosted(cn.creditNoteId)}
                              disabled={
                                cn.cancelledIndicator === "Yes" ||
                                loadingMap[cn.creditNoteId]
                              }
                              title="Mark as Posted"
                            >
                              {loadingMap[cn.creditNoteId] ? (
                                <div
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                              ) : (
                                <FaCheck />
                              )}
                            </Button> */}

                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleCancel(cn.creditNoteId)}
                              disabled={
                                cn.cancelledIndicator === "Yes" ||
                                loadingMap[cn.creditNoteId]
                              }
                              title="Cancel"
                            >
                              <FaTimes />
                            </Button>

                            <Button
                              variant="outline-info"
                              size="sm"
                              title="Print"
                              onClick={() => handlePrint(cn)}

                            >
                              <FaPrint />
                            </Button>

                            <Button
                              variant="outline-info"
                              size="sm"
                              title="Dispatch"
                              disabled={cn.inventoryPosted===1}
                              onClick={() =>
                                handleInventoryPosting(cn.creditNoteId)
                              }
                            >
                              <FaTruckFast />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="no-data text-center py-2">
                  {showCancelledOnly
                    ? "No cancelled credit notes found."
                    : "No credit notes of type Credit found."}
                </p>
              )}

              {/* DEBIT NOTES SECTION */}
              <h5 className="mb-2 mt-3">Debit Notes</h5>
              {debitTypeNotes.length > 0 ? (
                <Table bordered hover responsive className="data-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>CreditNote ID</th>
                      <th>Company Code</th>
                      <th>Type</th>
                      <th>Category</th>
                      <th>Customer Code</th>
                      <th>Against Invoice Number</th>
                      <th>Credit Note Date</th>
                      <th>Cancelled Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {debitTypeNotes.map((cn, idx) => (
                      <tr key={cn.creditNoteId || `debit-${idx}`}>
                        <td>{idx + 1}</td>
                        <td>{cn.creditNoteId}</td>
                        <td>{cn.companyCode}</td>
                        <td>{cn.transactionType}</td>
                        <td>{cn.category}</td>
                        <td>{cn.customerCode || "N/A"}</td>
                        <td>{cn.invoiveNumber || cn.invoiceNumber || "-"}</td>
                        <td>
                          {cn.creditNoteDate
                            ? moment(cn.creditNoteDate).format("L")
                            : "-"}
                        </td>
                        <td>
                          {cn.cancelledIndicator ??
                            cn.cancelled_Indicator ??
                            "-"}
                        </td>
                        <td>
                          <div className="d-flex gap-2">
                            {/* <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleEdit(cn)}
                              title="Edit"
                            >
                              <FaPencilAlt />
                            </Button>

                            <Button
                              variant="outline-success"
                              size="sm"
                              onClick={() => handleMarkPosted(cn.creditNoteId)}
                              disabled={
                                cn.cancelledIndicator === "Yes" ||
                                loadingMap[cn.creditNoteId]
                              }
                              title="Mark as Posted"
                            >
                              {loadingMap[cn.creditNoteId] ? (
                                <div
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                >
                                  <span className="visually-hidden">
                                    Loading...
                                  </span>
                                </div>
                              ) : (
                                <FaCheck />
                              )}
                            </Button> */}

                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleCancel(cn.creditNoteId)}
                              disabled={
                                cn.cancelledIndicator === "Yes" ||
                                loadingMap[cn.creditNoteId]
                              }
                              title="Cancel"
                            >
                              <FaTimes />
                            </Button>

                            <Button
                              variant="outline-info"
                              size="sm"
                              title="Print"
                             onClick={() => handlePrint(cn)}
                            >
                              <FaPrint />
                            </Button>

                            <Button
                              variant="outline-info"
                              size="sm"
                              title="Inventory Posting"
                              disabled={cn.inventoryPosted===true}
                              onClick={() =>
                                handleInventoryPosting(cn.creditNoteId)
                              }
                            >
                              <FaTruckFast />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="no-data text-center py-2">
                  {showCancelledOnly
                    ? "No cancelled debit notes found."
                    : "No credit notes of type Debit found."}
                </p>
              )}
            </>
          )}
        </div>
      </Container>
    </div>
  );
}
